import type { Except, Merge } from '@festina-life/core'
import {
  type CalendarDate,
  type DateValue,
  isSameDay,
} from '@internationalized/date'
import cx from 'classnames'
import {
  type ComponentProps,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  DatePicker,
  type DatePickerProps,
  I18nProvider,
} from 'react-aria-components'
import { useIntl } from 'react-intl'

import { useTranslate } from '../../hooks'
import { clearButtonStyles } from '../internal/clearButton/clearButtonStyles'
import { selectStyles } from '../select/selectStyles'
import type { RawTextInputProps } from '../textInput/RawTextInput'
import { Calendar } from './internal/Calendar'
import { DateButton } from './internal/DateButton'
import { DateClearButton } from './internal/DateClearButton'
import { DateField } from './internal/DateField'
import { DateGroup } from './internal/DateGroup'
import { DateIcon } from './internal/DateIcon'
import { DatePopover } from './internal/DatePopover'
import { useDateFieldState } from './internal/useDateFieldState'
import {
  type DateFieldGranularity,
  handleDatePickerPointerDown,
  hasPartialDateSegments,
  hasShortYear,
  isOutOfBounds,
  mapFromExternal,
  mapToExternal,
  normalizeDateValueForSegments,
} from './internal/utils'

export type RawDatePickerProps = Merge<
  Except<
    DatePickerProps<DateValue>,
    | 'minValue'
    | 'maxValue' // We call them "min" and "max" for better consistency with HTML input props
    | 'isDisabled' // We call it disabled elsewhere
    | 'isReadOnly' // Only disabled is supported
  > &
    Except<RawTextInputProps, 'type'>,
  {
    /** Value date in server format, assuming UTC+00:00. */
    value?: string
    /** Callback for value date changes in server format, assuming UTC+00:00. */
    onChange?: (value: string) => void
    min?: string
    max?: string
    onBlur?: (value: string) => void
    granularity?: DateFieldGranularity
    /** Wraps the input inside the date picker. Supply aria-labelledby when including a visible React Aria Label. */
    renderInput?: (input: ReactNode) => ReactNode
  }
>

export const RawDatePicker = ({
  isInError = false,
  disabled = false,
  required,
  value: valueExternal,
  onChange: onChangeExternal,
  min,
  max,
  onBlur,
  granularity = 'day',
  placeholderValue: placeholderValueExternal,
  renderInput,
  ...props
}: RawDatePickerProps) => {
  const { locale } = useIntl()
  const translate = useTranslate()
  const [calendarFocusedDate, setCalendarFocusedDate] = useState<CalendarDate>()
  const isArrowStepRef = useRef(false)
  const {
    internalValue,
    isPartial,
    dateFieldKey,
    change,
    markPartial,
    completePartial,
    clear,
  } = useDateFieldState(valueExternal, () => {
    isArrowStepRef.current = false
  })

  const placeholderValue = normalizeDateValueForSegments(
    placeholderValueExternal,
    granularity,
  )
  // React Aria drops partially typed segments whenever the value identity changes.
  const value = useMemo(
    () => mapFromExternal(internalValue || undefined, granularity) ?? null,
    [internalValue, granularity],
  )
  // A remembered calendar focus on another day is stale, e.g. after typing a new date.
  const calendarDefaultFocusedValue =
    calendarFocusedDate && (!value || isSameDay(calendarFocusedDate, value))
      ? calendarFocusedDate
      : undefined
  const shouldShowButtonAndPopover =
    granularity === 'day' || granularity === 'minute'

  const handleDateGroupKeyDownCapture = (event: KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLElement
    const hasFocusedDateSegment = !!target.closest('[role="spinbutton"]')

    if (!hasFocusedDateSegment) {
      return
    }

    isArrowStepRef.current =
      event.key === 'ArrowUp' || event.key === 'ArrowDown'
  }

  const handleDateGroupBlurCapture = (event: FocusEvent<HTMLElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null
    if (event.currentTarget.contains(nextFocusedElement)) {
      return
    }

    if (hasPartialDateSegments(event.currentTarget)) {
      if (internalValue && !isPartial) {
        onChangeExternal?.('')
      }
      markPartial()
      onBlur?.('')
      return
    }

    const valueOnBlur = mapToExternal({ value, granularity })

    // When partial segments are completed back to the held value, React Aria emits no change.
    if (isPartial || (hasShortYear(value) && valueOnBlur !== valueExternal)) {
      completePartial()
      onChangeExternal?.(valueOnBlur)
    }
    onBlur?.(valueOnBlur)
  }

  const handleOnChange = (value: DateValue | null) => {
    const isArrowStep = isArrowStepRef.current
    isArrowStepRef.current = false

    if (isArrowStep && isOutOfBounds({ value, granularity, min, max })) {
      return
    }

    const mappedValue = mapToExternal({ value, granularity })
    change(mappedValue)
    if (!hasShortYear(value)) {
      onChangeExternal?.(mappedValue)
    }
  }

  const handleClear = () => {
    clear()
    if (valueExternal) {
      onChangeExternal?.('')
    }
  }

  const dateInput = (
    <DateGroup
      isInError={isInError || isPartial}
      isDisabled={disabled}
      hasClearButton={!disabled}
      onBlurCapture={handleDateGroupBlurCapture}
      onKeyDownCapture={handleDateGroupKeyDownCapture}
      onPointerDownCapture={handleDatePickerPointerDown}
    >
      <DateField
        key={dateFieldKey}
        isInError={isInError || isPartial}
        isDisabled={disabled}
      />
      <div className={selectStyles.inputIconsWrapper}>
        {!disabled && <DateClearButton onClear={handleClear} />}
        {shouldShowButtonAndPopover ? (
          <DateButton
            isInError={isInError || isPartial}
            isDisabled={disabled}
          />
        ) : (
          <DateIcon />
        )}
      </div>
    </DateGroup>
  )

  return (
    <I18nProvider locale={`${locale}-u-ca-gregory`}>
      <DatePicker
        {...props}
        className={cx(clearButtonStyles.group, props.className)}
        granularity={
          // The types lie a bit about what the DatePicker accepts of granularity, but it does work with our extended DateFieldGranularity type as well
          granularity as ComponentProps<typeof DatePicker>['granularity']
        }
        isDisabled={disabled}
        isInvalid={isPartial || props.isInvalid}
        isRequired={required ?? props.isRequired}
        minValue={mapFromExternal(min, granularity)}
        maxValue={mapFromExternal(max, granularity)}
        onChange={handleOnChange}
        placeholderValue={placeholderValue}
        value={value}
        aria-label={
          props['aria-label'] ??
          (props['aria-labelledby'] ? undefined : translate('datePicker.label'))
        }
        shouldForceLeadingZeros // Looks nicer (less jumps)
        hideTimeZone // Festina doesn't do time zones
      >
        {renderInput ? renderInput(dateInput) : dateInput}
        {shouldShowButtonAndPopover && (
          <DatePopover>
            <Calendar
              defaultFocusedValue={calendarDefaultFocusedValue}
              onFocusChange={setCalendarFocusedDate}
            />
          </DatePopover>
        )}
      </DatePicker>
    </I18nProvider>
  )
}
